import { useState, useEffect, useRef, useCallback } from 'react';
import { FALLBACK_RATES } from '../data/currencies.js';

const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes
let cachedRates = null;
let cacheTimestamp = 0;

export function useExchangeRates() {
  const [rates, setRates] = useState(FALLBACK_RATES);
  const [isLive, setIsLive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  const fetchRates = useCallback(async () => {
    const now = Date.now();
    if (cachedRates && (now - cacheTimestamp) < CACHE_DURATION) {
      setRates(cachedRates);
      setIsLive(true);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch('https://open.er-api.com/v6/latest/USD', {
        signal: AbortSignal.timeout(8000)
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.result === 'success' && data.rates) {
        cachedRates = data.rates;
        cacheTimestamp = now;
        if (mountedRef.current) {
          setRates(data.rates);
          setIsLive(true);
        }
      } else {
        throw new Error('Invalid response');
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err.message);
        setRates(FALLBACK_RATES);
        setIsLive(false);
      }
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchRates();
    const interval = setInterval(fetchRates, CACHE_DURATION);
    return () => {
      mountedRef.current = false;
      clearInterval(interval);
    };
  }, [fetchRates]);

  const convert = useCallback((amountInBase, baseCurrency, targetCurrency) => {
    if (!amountInBase || baseCurrency === targetCurrency) return amountInBase;
    const baseRate = rates[baseCurrency] || FALLBACK_RATES[baseCurrency] || 1;
    const targetRate = rates[targetCurrency] || FALLBACK_RATES[targetCurrency] || 1;
    return (amountInBase / baseRate) * targetRate;
  }, [rates]);

  return { rates, isLive, isLoading, error, convert, refetch: fetchRates };
}